import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { API_BASE } from "../config";
import { track } from "../track";
import OrderSheet, { type Mailed } from "./OrderSheet";
import { PageContext } from "./page";
import type { LeadKind, PreviewDoc, PriceOffer } from "../types";
import type { Words } from "./copy";
import { bill, fill } from "./multiBuy";
import { billIn, ownsQuantity, qtyKey, unbroken, unitsOf } from "./Quantity";

/** A size tapped in a "By size" ladder outside the button: open the sheet on
 *  it, on the payment form where the page takes the payment. `n` counts the
 *  taps, so the same size tapped again (after the sheet was closed) opens it
 *  again. */
export interface SizeAsk {
  size: string;
  n: number;
}

/** The decision, parked where the thumb already is.
 *
 *  The button used to sit inline and scroll away, so by the time someone had
 *  read everything there was nothing to press. It is the one order button of
 *  the page now: a second copy of it after "Love it" was tried, and two
 *  identical buttons in one view read as a mistake. The price rides in the label
 *  because the number is the decision, and it says "from", because it is the
 *  smallest size and Etsy's size picker goes up from there. Once they have
 *  picked a size, here or on a "By size" pill, on this visit or an earlier
 *  one on this device, the label names it with its own price instead: most
 *  orders happen on a later visit, and the button they come back to should
 *  already say what they decided.
 *
 *  It says "I want to order" rather than sending them to the shop. Etsy is
 *  where most of the people who pressed it stopped, and pressing this now tells
 *  the server first: it mails them the listing and the steps, and buzzes the
 *  owner. The sheet it opens has the same steps and the way out to Etsy, so
 *  whoever is ready loses nothing by it.
 *
 *  On a page of several pieces that shows them as cards there is one of these
 *  per piece instead of one in the bar, each pointing at that product's own
 *  listing. A single bar cannot serve three alternatives: it would have to
 *  pick one of them, and the two people who wanted the other two would arrive
 *  at the wrong listing. A page that shows one piece at a time gives the bar
 *  that piece (`pieceId`, `offer`, `etsyUrl`, `pieceKind`), so the bar orders
 *  exactly what is on screen, and the page makes a new bar of it whenever the
 *  piece changes.
 */
export default function OrderBar({
  preview,
  words: t,
  from,
  offer,
  etsyUrl,
  pieceKind,
  pieceId,
  drawingUrl,
  autoOpen,
  sizeAsk,
}: {
  /** The lead's own offer, for a page of one piece. */
  preview?: PreviewDoc;
  words: Words;
  from: "bar" | "piece";
  /** A piece's own price and listing, which win over the lead's when given. */
  offer?: PriceOffer | null;
  etsyUrl?: string;
  pieceKind?: LeadKind;
  /** Which piece, so the mail carries that product's listing and price. */
  pieceId?: string;
  /** The drawing of what they are buying, for the sheet's first step. On a
   *  page of one piece it is read from the preview when not given. */
  drawingUrl?: string | null;
  /** Open the sheet as soon as this is true, once: the order mail's "Pay on my
   *  page" lands here. The mail already went, so nothing is asked of the
   *  server, and the sheet opens on the form rather than on the size. */
  autoOpen?: boolean;
  /** The ladder's tap, for the sheet this button opens. */
  sizeAsk?: SizeAsk | null;
}) {
  const page = useContext(PageContext);
  const [open, setOpen] = useState(false);
  const [mailed, setMailed] = useState<Mailed>(null);
  // The size the sheet opens on when a ladder opened it; the sheet's own
  // choice otherwise.
  const [picked, setPicked] = useState<string | undefined>(undefined);
  // Opened by the order mail's link: straight to the form.
  const [direct, setDirect] = useState(false);
  const asked = useRef(false);
  const close = useCallback(() => {
    setOpen(false);
    setDirect(false);
  }, []);
  const button = useRef<HTMLButtonElement>(null);
  const price = offer ?? preview?.price ?? null;
  const href = etsyUrl ?? preview?.etsyUrl;
  // Only an offer with its sizes can be paid for in the sheet. Without them the
  // sheet would open on the Etsy steps, which is not what "Pay on my page"
  // promised; the button is there for that.
  const payable = !!price?.sizes?.length;
  const opened = useRef(false);
  useEffect(() => {
    if (!autoOpen || !payable || opened.current) return;
    opened.current = true;
    asked.current = true;
    // On a page of several, the card is somewhere down the page. Brought to
    // the middle of the screen first, so that closing the sheet leaves them
    // at the piece they came to pay for rather than at the top of the page.
    if (from === "piece") button.current?.scrollIntoView({ block: "center" });
    setDirect(true);
    setOpen(true);
  }, [autoOpen, payable, from]);
  // The size they picked, with its price, once there is one; "from" the
  // smallest until then. Only on a page of one piece, whose size the page
  // keeps: each piece of several keeps its own in its own sheet.
  const sized =
    from !== "piece" && page?.size ? price?.sizes?.find((s) => s.size === page.size) ?? null : null;
  // They asked for the form before, on this device: the button offers to
  // continue there, and the tap opens the sheet on it. A session is made on
  // that tap and no earlier, which is the person asking to pay.
  const resume = !!sized && page?.step === "pay" && !!page?.checkout;
  // More than one of it, chosen in the sheet on this visit, where the page
  // decides how many (the multi-buy): the button says how many and what they
  // all come to, the same total the sheet it opens shows and the form sends,
  // rather than the price of one above a form for two. Each kept whole: a
  // longer label wraps, and "×2" alone at the start of a line, or "€", says
  // nothing.
  const units = sized && page?.checkout && ownsQuantity(price) ? unitsOf(page.qtys?.[qtyKey(from, pieceId)]) : 1;
  const several = sized && units > 1 ? fill(price?.format ?? "", bill([billIn(sized, units)]).total) : null;
  const label = sized
    ? t.t(
        resume ? "orderContinue" : "orderSized",
        several
          ? { size: unbroken(`${sized.size} ×${units}`), price: unbroken(several) }
          : { size: unbroken(sized.size), price: unbroken(sized.now) }
      )
    : price?.now
      ? `${t.t("wantOrder")}\u00a0· ${t.t("fromPrice", { price: unbroken(price.now) })}`
      : t.t("wantOrder");
  // Paid for, or being paid: the button would only take the same money twice.
  // The sheet it opened stays until they close it, because it is the one
  // saying thank you. The studio's look keeps it, to try the checkout on a
  // page that has sold.
  const done = !!page?.ordered && !page.admin;

  // Tapping a size is wanting to order it: the same as the button, on that size.
  // A tap that was already there when this button was made (the page swapped
  // the piece it shows, and so the button) is not a tap on this one.
  const seenAsk = useRef(sizeAsk?.n ?? 0);
  useEffect(() => {
    if (!sizeAsk || sizeAsk.n === seenAsk.current) return;
    seenAsk.current = sizeAsk.n;
    if (!href || done || !payable) return;
    track("checkout.size", { size: sizeAsk.size, from: "ladder" });
    want(sizeAsk.size);
  }, [sizeAsk?.n]);
  if (!href) return null;

  function want(size?: string) {
    setPicked(size);
    // Straight to the form for someone who had reached it before and taps
    // the button that says so. A size tapped in the page's own ladder is the
    // same: they have seen the sizes and what each costs, that row is the
    // choice, and the first step would only show them the size again. The form
    // is a step they have now asked for, so the button offers to continue
    // there next time. A piece of several keeps its own ladder and its own
    // first step. The sheet still stops on the first step where it has
    // something to ask first (the Christmas base, not yet answered), and the
    // button with no size keeps the first step.
    const toForm = !!size && from !== "piece" && payable && !!page?.checkout;
    setDirect(toForm || (resume && !size));
    if (toForm) page?.onStep?.("pay");
    setOpen(true);
    // Once per button per visit. The server would not mail twice anyway; this
    // spares it the question. The studio's own look asks nothing at all.
    if (asked.current || !page || page.admin) return;
    asked.current = true;
    void fetch(`${API_BASE}/api/order-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: page.token, piece: pieceId ?? "", from }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: { mailed?: Mailed }) => setMailed(data.mailed ?? null))
      .catch(() => {
        // Nothing is said about a mail that may not have gone. The steps and
        // the button are on the sheet either way, and pressing it again asks
        // again.
        asked.current = false;
      });
  }

  // Their drawing, for the head of the sheet's first step: the piece's when
  // given, else the page's own (a page of one piece files its drawing under
  // no piece at all, and any mockup on it is the one).
  const drawing = drawingUrl ?? preview?.artifacts.find((a) => a.kind === "mockup")?.url ?? null;

  return (
    <>
      {!done && (
        <button ref={button} className="btn btn-primary btn-block btn-lift" onClick={() => want()}>
          {label}
        </button>
      )}
      {open && (
        <OrderSheet
          words={t}
          etsyUrl={href}
          offer={price}
          delivery={preview?.delivery ?? null}
          // An empty code is a lead sent with none, so empty counts as not there.
          promoCode={page?.promoCode || preview?.promoCode || ""}
          mailed={mailed}
          from={from}
          pieceKind={pieceKind}
          // Whose price the sizes are: the piece's, or on a page of one the
          // lead's, as the offer above them is.
          kind={pieceKind ?? preview?.kind}
          pieceId={pieceId}
          initialSize={picked}
          drawingUrl={drawing}
          payNow={direct}
          onClose={close}
        />
      )}
    </>
  );
}
